// ─── Comp + Rent-Roll Extraction Service ───────────────────────────────────
// Shared extractor used by:
//   - CompsView "Upload PDF" flow (existing) — populates the global comp library
//   - DealCompSet "Extract from Document" flow (new) — populates a deal's
//     assigned comps and rent roll
//
// Wraps three steps:
//   1) Get the document text (PDF/XLSX → string via extractTextFromFile)
//   2) Call Claude with the comp-extraction system prompt
//   3) Parse + repair the JSON response (Claude sometimes truncates large
//      responses mid-array; we walk back to the last complete entry and
//      re-close brackets so we keep whatever did make it)
//
// Returns the raw extracted shape; dedup + persistence happen at the caller.

import { callClaudeWithMessages } from "./claude";
import { extractTextFromFile } from "../supabase";

const MAX_INPUT_CHARS = 200_000;
const MAX_OUTPUT_TOKENS = 16_384;

export const COMP_EXTRACTION_PROMPT = `You are a commercial real estate data extraction specialist. Your job is to extract EVERY lease and EVERY sale from the document into a single combined list.

THERE ARE THREE TYPES OF SOURCES THAT EACH BECOME LEASE COMPS:

1. RENT ROLL (a table of in-place tenants for the SUBJECT PROPERTY of the OM).
   This is the most commonly missed source. A rent roll lists each tenant
   currently occupying the building. EVERY tenant row is a lease comp.
   - Headers to look for: "Rent Roll", "Stacking Plan", "Tenant Roster",
     "Lease Schedule", "Tenancy Schedule".
   - Row signals: Unit/Suite, Tenant, RSF/SF, Lease Start, Lease End, Rent
     ($/SF or annual), Lease Type, Escalations.
   - Skip rows where Tenant is "VACANT", "Vacant", or blank.
   - The address is NOT in each row — use the SUBJECT PROPERTY address from
     the OM cover page / executive summary for every rent-roll row.

2. LEASE COMPS section (a table of comparable leases at OTHER buildings).
   - Headers (case-insensitive, any of these): "Lease Comps", "Lease Comparables",
     "Lease Comparable Set", "Market Comps", "Comparable Leases", "Comp Set —
     Leases", "Recent Leases", "Leasing Comps". OMs sometimes use the page
     title alone (e.g. a section labeled simply "Lease Comps") — treat that
     as a definitive marker even if the table headers are slightly different.
   - Each row has its own address.
   - Extract EVERY row, not just the first few. Lease comp tables often
     span multiple pages; keep going until the section ends.

3. SALE COMPS section (comparable sales at other buildings).
   - Headers (case-insensitive, any of these): "Sale Comps", "Sale Comparables",
     "Sales Comparables", "Sales Comp Set", "Comparable Sales", "Recent Sales",
     "Comp Set — Sales", "Investment Sale Comps", "Transaction Comps".
   - Extract EVERY row, not just the first few.

Return ONLY valid JSON in this exact format:
{
  "subject_property": {
    "address": "...",
    "city": "...",
    "state": "...",
    "market": "...",
    "property_name": "..."
  },
  "lease_comps": [
    {
      "source": "rent_roll" or "lease_comp",
      "property_name": "...",
      "address": "...",
      "city": "...",
      "state": "...",
      "market": "...",
      "micro_market": "..." or null,
      "tenant": "...",
      "tenant_industry": "...",
      "building_sf": null or number,
      "suite_sf": null or number,
      "lease_rate": null or number (annual $/SF),
      "lease_type": "NNN" or "Gross" or "Modified Gross",
      "comp_date": "YYYY-MM-DD" or null (use Lease Start date for rent-roll rows),
      "lease_start": "YYYY-MM-DD" or null,
      "lease_end": "YYYY-MM-DD" or null,
      "walt_years": null or number (years remaining as of today),
      "landlord": "...",
      "loading_type": "Front" or "Rear" or "Front/Rear" or "Side" or null,
      "door_type": "Grade" or "Street-Level" or "Dock" or "Dock-High" or null,
      "year_built": "..." or null,
      "clear_height": "..." or null,
      "suite_id": "..." or null (rent-roll only: "Suite 100", "Bldg A"),
      "notes": "..."
    }
  ],
  "sale_comps": [
    {
      "property_name": "...",
      "address": "...",
      "city": "...",
      "state": "...",
      "market": "...",
      "micro_market": "..." or null,
      "buyer": "...",
      "seller": "...",
      "sale_price": null or number,
      "price_psf": null or number,
      "building_sf": null or number,
      "cap_rate": null or number (as percentage, e.g. 6.5),
      "comp_date": "YYYY-MM-DD" or null,
      "portfolio_name": "..." or null,
      "occupancy_at_closing": null or number,
      "walt_years": null or number,
      "year_built": "..." or null,
      "clear_height": "..." or null,
      "office_finish_pct": null or number,
      "status_detail": "..." or null,
      "notes": "..."
    }
  ]
}

SPECIAL CASE — UW (Underwriting) workbooks:
- Detectable by "UW" in the file name AND/OR a sheet literally named "Rent Roll".
- The Rent Roll sheet is the ONLY rent-roll source — every tenant row sits in the first 250 rows of that sheet. Extract every non-vacant row as a lease_comp with source="rent_roll".
- UW workbooks generally have NO sale comp or lease comp sections (those live in OMs, not UW). If there's no obvious comp section, return empty lease_comps (other than rent-roll rows) and sale_comps arrays — do NOT invent comps from assumptions / cash flow / acquisition sheets.
- The subject_property may be on the cover sheet or in an "Acquisition Summary" sheet. If the subject address can't be confidently identified, set it to "" and the team will fill it in.

Rules:
- subject_property = the building the OM is selling. Find it on the cover or executive summary.
- For rent_roll rows: copy the SAME subject_property address/city/state/market into every row's address/city/state/market, and include the suite_id field.
- For lease_comp rows (separate comp section): use that row's own address; leave suite_id null.
- Skip vacant rent-roll rows (Tenant = "VACANT" or blank).
- Extract ALL rent-roll rows regardless of Lease Start date. Every signed lease is a comp.
- DEDUP RULE — if the SAME tenant appears in BOTH the rent roll AND the lease comps section (e.g. the OM uses a subject-property tenant as a comparable), return that tenant ONCE with source="rent_roll" and DO NOT also include it as a lease_comp. Match by tenant name + address (the rent roll uses the subject property address, so any lease_comp row whose address matches the subject property AND whose tenant matches a rent-roll tenant is the same lease).
- If lease_rate looks like monthly $/SF (small number like 2-5), multiply by 12 to get annual.
- If lease_rate is total annual rent in dollars, divide by suite_sf to get $/SF.
- Cap rates as percentages (e.g. 6.5 not 0.065).
- Dates in YYYY-MM-DD format. Excel serial numbers (like 45261) need converting: 1900-01-01 + (n - 2) days.
- MARKET vs MICRO-MARKET: if the sheet has BOTH a "Sub Market" / "Submarket" column AND a "Micro Market" / "Micromarket" column, set "market" = the BROADER one and "micro_market" = the NARROWER. If only one tier exists, put it in "market" and leave "micro_market" null.
- NOTES: the "notes" field MUST contain the FULL comments / remarks paragraph from the sheet, verbatim, including line breaks. Do NOT summarize or shorten.
- year_built: keep the raw string form. "1987" stays "1987"; "1981-1986" stays "1981-1986"; "1977 / 2021" (year built / renovated) stays as-is.
- clear_height: keep the raw string form. "18'-24'" stays as-is.
- Return empty arrays if a category has no entries.
- Do NOT include any text outside the JSON. No prose, no code fences.`;

// Parse Claude's JSON response, with a repair pass for truncated arrays.
export function parseExtractionResponse(raw) {
  let cleaned = raw.replace(/```json?\s*/gi, "").replace(/```/g, "").trim();
  const firstBrace = cleaned.indexOf("{");
  const lastBrace = cleaned.lastIndexOf("}");
  if (firstBrace >= 0 && lastBrace > firstBrace) {
    cleaned = cleaned.slice(firstBrace, lastBrace + 1);
  }
  try {
    return { parsed: JSON.parse(cleaned), truncated: false };
  } catch (firstErr) {
    // Likely truncated mid-array. Walk back to the last `},`, then re-close
    // any open arrays / objects so we keep whatever made it.
    const lastEntryEnd = cleaned.lastIndexOf("},");
    if (lastEntryEnd <= 0) throw firstErr;
    let salvaged = cleaned.slice(0, lastEntryEnd + 1);
    let openSquare = 0, openCurly = 0, inStr = false, esc = false;
    for (const ch of salvaged) {
      if (esc) { esc = false; continue; }
      if (ch === "\\") { esc = true; continue; }
      if (ch === '"') { inStr = !inStr; continue; }
      if (inStr) continue;
      if (ch === "[") openSquare++;
      else if (ch === "]") openSquare--;
      else if (ch === "{") openCurly++;
      else if (ch === "}") openCurly--;
    }
    salvaged += "]".repeat(Math.max(0, openSquare)) + "}".repeat(Math.max(0, openCurly));
    return { parsed: JSON.parse(salvaged), truncated: true };
  }
}

// Full pipeline: text extract → Claude → parse. Returns the normalized
// extraction shape. Throws on text-extraction failure or Claude error.
export async function extractCompsFromFile(file, onProgress = () => {}) {
  // UW workbooks are detected by "UW" in the file name (Daniel's convention).
  // ONLY for those files do we apply the Rent-Roll-sheet-first + 250-row
  // cap logic, since other xlsx files (CoStar exports, ad-hoc comp sheets,
  // etc.) might legitimately have a sheet named "Rent Roll" that means
  // something different.
  const isUW = /\bUW\b/i.test(file.name || "");

  onProgress("Extracting text from document…");
  const text = await extractTextFromFile(file, { prioritizeRentRoll: isUW });
  if (!text || text.length < 50) {
    throw new Error("Could not extract enough text from this document.");
  }
  const truncatedText = text.length > MAX_INPUT_CHARS ? text.slice(0, MAX_INPUT_CHARS) : text;
  if (text.length > MAX_INPUT_CHARS) {
    console.warn(`Document text was ${text.length} chars; truncated to ${MAX_INPUT_CHARS} before sending to Claude.`);
  }

  onProgress("Analyzing document with AI…");
  // Pass the file name through so Claude can apply UW-specific extraction
  // rules (Rent Roll sheet only, no invented comps) — same detection rule
  // the reader uses: 'UW' in the filename.
  const fileHeader = `File name: ${file.name || "(unnamed)"}${isUW ? "  ← filename contains 'UW' → treat as Underwriting workbook (rent roll lives in the 'Rent Roll' sheet, first 250 rows)" : ""}`;
  const raw = await callClaudeWithMessages(
    COMP_EXTRACTION_PROMPT,
    [{ role: "user", content: `${fileHeader}\n\nExtract all comps from this document:\n\n${truncatedText}` }],
    MAX_OUTPUT_TOKENS
  );

  onProgress("Parsing extraction response…");
  const { parsed, truncated } = parseExtractionResponse(raw);
  return {
    subject_property: parsed.subject_property || {},
    sale_comps: parsed.sale_comps || [],
    lease_comps: parsed.lease_comps || [],
    truncated,
  };
}
